import * as Sentry from '@sentry/react';
import appConfig from 'appConfig';
import { getAppSource, getShellInfo } from './shellDetection';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED !== 'false';
const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development';
const sentryProject = import.meta.env.VITE_SENTRY_PROJECT;
// Keep release aligned with the app's existing version format so
// version analytics and update workflows remain unchanged.
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE || appConfig.version || undefined;

// --- Runtime kill-switch ---
// Persists across page reloads via localStorage so an admin can disable Sentry
// mid-session and it stays off until they explicitly re-enable it.
export const SENTRY_DISABLED_STORAGE_KEY = 'oms_sentry_disabled';

let sentryRuntimeDisabled = (() => {
  try {
    return localStorage.getItem(SENTRY_DISABLED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
})();

export const isSentryDisabled = (): boolean => sentryRuntimeDisabled;

type SentryDisabledListener = (disabled: boolean) => void;
const sentryDisabledListeners: SentryDisabledListener[] = [];

/** Subscribe to runtime Sentry enabled/disabled changes. Returns an unsubscribe function. */
export const onSentryDisabledChange = (listener: SentryDisabledListener): (() => void) => {
  sentryDisabledListeners.push(listener);
  return () => {
    const idx = sentryDisabledListeners.indexOf(listener);
    if (idx > -1) sentryDisabledListeners.splice(idx, 1);
  };
};

export const setSentryDisabled = (disabled: boolean): void => {
  sentryRuntimeDisabled = disabled;
  try {
    if (disabled) {
      localStorage.setItem(SENTRY_DISABLED_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(SENTRY_DISABLED_STORAGE_KEY);
    }
  } catch {}
  // Best-effort stop/resume the session replay integration.
  try {
    const replay = (Sentry as any).getReplay?.();
    if (disabled) {
      replay?.stop?.();
    } else {
      replay?.start?.();
    }
  } catch {}
  // Notify all subscribers (e.g. Admin page UI, future components).
  sentryDisabledListeners.forEach(l => l(disabled));
};

type SentryWithOptionalIntegrations = typeof Sentry & {
  browserTracingIntegration?: () => unknown;
  replayIntegration?: () => unknown;
  consoleLoggingIntegration?: (config: { levels: string[] }) => unknown;
};

const sentrySdk = Sentry as SentryWithOptionalIntegrations;

let initialized = false;

const parseSampleRate = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.min(1, Math.max(0, parsed));
};

const buildIntegrations = (): any[] => {
  const integrations: any[] = [];

  if (typeof sentrySdk.browserTracingIntegration === 'function') {
    integrations.push(sentrySdk.browserTracingIntegration());
  }

  if (typeof sentrySdk.replayIntegration === 'function') {
    integrations.push(sentrySdk.replayIntegration());
  }

  // consoleLoggingIntegration routes console.* calls to the Sentry Logs tab
  // (structured log entries), NOT to Issues. This is distinct from the removed
  // captureConsoleIntegration which incorrectly created Issues from info/warn calls.
  // Intentionally limited to warn/error only: console.log/info calls frequently
  // include request config objects that contain Authorization headers (JWT tokens).
  if (typeof sentrySdk.consoleLoggingIntegration === 'function') {
    integrations.push(sentrySdk.consoleLoggingIntegration({ levels: ['warn', 'error'] }));
  }

  return integrations;
};

export const initSentry = (): void => {
  if (initialized || !sentryDsn || !sentryEnabled) {
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: sentryRelease,
    integrations: buildIntegrations(),
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, import.meta.env.DEV ? 1.0 : 0.2),
    replaysSessionSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, import.meta.env.DEV ? 1.0 : 0.1),
    replaysOnErrorSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1.0),
    // Enable the Sentry Logs pipeline (Logs tab). Required for consoleLoggingIntegration
    // and Sentry.logger.* calls to produce log entries rather than Issues.
    enableLogs: true,
    beforeBreadcrumb(breadcrumb) {
      // Strip Authorization tokens from HTTP request breadcrumbs added by
      // browserTracingIntegration. By default Sentry does not include headers,
      // but this guards against SDK version differences and future config changes.
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        const h = breadcrumb.data?.headers ?? breadcrumb.data?.requestHeaders;
        if (h && typeof h === 'object') {
          for (const key of Object.keys(h)) {
            if (key.toLowerCase() === 'authorization') {
              h[key] = '[Filtered]';
            }
          }
        }
      }
      return breadcrumb;
    },
    beforeSend(event) {
      if (sentryRuntimeDisabled) return null;
      if (sentryProject) {
        event.tags = { ...(event.tags ?? {}), sentryProject };
      }
      // Scrub Authorization headers from the request context that Sentry
      // serializes when capturing error events.
      if (event.request?.headers && typeof event.request.headers === 'object') {
        for (const key of Object.keys(event.request.headers)) {
          if (key.toLowerCase() === 'authorization') {
            (event.request.headers as Record<string, string>)[key] = '[Filtered]';
          }
        }
      }
      return event;
    },
    beforeSendTransaction(event) {
      if (sentryRuntimeDisabled) return null;
      return event;
    }
  });

  const appSource = getAppSource();
  Sentry.setTag('appSource', appSource);
  if (appConfig.version) {
    Sentry.setTag('appVersion', appConfig.version);
  }
  Sentry.setContext('shellInfo', getShellInfo() as any);

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const sentryLogger = (Sentry as any).logger;
    const debugRoot = (window as any).debug || {};
    debugRoot.sentry = {
      // --- Issues pipeline (appears in Sentry > Issues tab) ---
      captureTestMessage: (message = '[test] Sentry test message') =>
        Sentry.captureMessage(message, 'info'),
      captureTestWarning: (message = '[test] Sentry test warning') =>
        Sentry.captureMessage(message, 'warning'),
      captureTestError: () => {
        const error = new Error('[test] Sentry test error');
        Sentry.captureException(error);
        return error.message;
      },
      // --- Logs pipeline (appears in Sentry > Logs tab) ---
      logTestTrace: (message = '[test] Sentry trace log') =>
        sentryLogger?.trace(message, { test: true, source: 'debug-helper' }),
      logTestInfo: (message = '[test] Sentry info log') =>
        sentryLogger?.info(message, { test: true, source: 'debug-helper' }),
      logTestWarn: (message = '[test] Sentry warn log') =>
        sentryLogger?.warn(message, { test: true, source: 'debug-helper' }),
      logTestError: (message = '[test] Sentry error log') =>
        sentryLogger?.error(message, { test: true, source: 'debug-helper' }),
      // --- Metrics (appears in Sentry > Metrics tab) ---
      emitTestMetric: (name = 'oms.debug.test.count') => {
        const metrics = (Sentry as any).metrics;
        if (metrics) {
          metrics.count(name, 1, { attributes: { test: 'true', source: 'debug-helper' } });
          return `emitted metric: ${name}`;
        }
        return 'metrics API not available';
      },
      // --- Console log capture (goes to Logs tab via consoleLoggingIntegration) ---
      consoleTestInfo: (message = '[test] console.info captured by Sentry') => console.info(message),
      consoleTestWarn: (message = '[test] console.warn captured by Sentry') => console.warn(message),
      // --- Diagnostics ---
      lastEventId: () => Sentry.lastEventId(),
      isDisabled: () => sentryRuntimeDisabled,
      captureUnique: () => {
        const error = new Error(`[test] unique ${Date.now()}`);
        Sentry.captureException(error);
        const id = Sentry.lastEventId();
        console.info(`[Sentry] lastEventId: ${id ?? '(null — beforeSend dropped it)'}`);
        return id;
      },
      // --- Help ---
      help: () => console.table({
        'captureTestMessage()': 'Issues tab — info-level event',
        'captureTestWarning()': 'Issues tab — warning-level event',
        'captureTestError()': 'Issues tab — exception (may dedup with existing issue)',
        'captureUnique()': 'Issues tab — unique timestamped error, logs event ID',
        'lastEventId()': 'Returns last accepted event ID (null = beforeSend dropped it)',
        'isDisabled()': 'Returns current in-memory sentryRuntimeDisabled flag',
        'logTestInfo()': 'Logs tab — info log via Sentry.logger',
        'logTestWarn()': 'Logs tab — warn log via Sentry.logger',
        'logTestError()': 'Logs tab — error log via Sentry.logger',
        'consoleTestInfo()': 'Logs tab — via console.info (consoleLoggingIntegration)',
        'consoleTestWarn()': 'Logs tab — via console.warn (consoleLoggingIntegration)',
        'emitTestMetric()': 'Metrics tab — counter increment',
      })
    };
    (window as any).debug = debugRoot;
    console.info('[Sentry] Debug helpers ready. Run window.debug.sentry.help() for the full list.');
  }

  initialized = true;
};

export const setSentryUser = (userName: string | null, userEmail: string | null, userId?: string): void => {
  Sentry.setUser(
    userId || userName || userEmail
      ? {
          id: userId,
          username: userName ?? undefined,
          email: userEmail ?? undefined
        }
      : null
  );
};

export const clearSentryUser = (): void => {
  Sentry.setUser(null);
};
